import { Component, Input } from '@angular/core';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, CompanyResponse, CoOrganiserAccessLevel } from '../../../../../../../../../../@shared/models/response.module';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-co-organiser-modal',
  standalone: false,
  templateUrl: './add-co-organiser-modal.html',
  styleUrl: './add-co-organiser-modal.css'
})
export class AddCoOrganiserModal {
  @Input() event: EventResponse;
  @Input() existingCoOrganiserIds: string[] = [];

  // Company search
  companySearch = '';
  companySearchResults: CompanyResponse[] = [];
  selectedCompany: CompanyResponse | null = null;
  isSearching = false;
  showResults = false;
  private searchSubject = new Subject<string>();

  // Access level
  accessLevel: CoOrganiserAccessLevel = 'limited';
  accessLevelOptions: { value: CoOrganiserAccessLevel; label: string; description: string }[] = [
    { value: 'full', label: 'manageEvent.coOrganisers.accessFull', description: 'manageEvent.coOrganisers.accessFullDesc' },
    { value: 'limited', label: 'manageEvent.coOrganisers.accessLimited', description: 'manageEvent.coOrganisers.accessLimitedDesc' },
    { value: 'view', label: 'manageEvent.coOrganisers.accessView', description: 'manageEvent.coOrganisers.accessViewDesc' }
  ];

  submitted: boolean = false;
  saving: boolean = false;

  constructor(
    private eventsService: EventsService,
    private modal: NgbActiveModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(search => {
      this.performSearch(search);
    });
  }

  onSearchInput(value: string) {
    this.companySearch = value;
    if (value.length >= 2) {
      this.searchSubject.next(value);
    } else {
      this.companySearchResults = [];
      this.showResults = false;
    }
  }

  performSearch(search: string) {
    if (search.length < 2) return;
    this.isSearching = true;
    this.eventsService.searchCompaniesForCoOrganiser(search, this.event.event_id).subscribe({
      next: (res: CompanyResponse[]) => {
        // Filter out companies that are already co-organisers
        this.companySearchResults = res.filter(c => !this.existingCoOrganiserIds.includes(c.company_id));
        this.showResults = true;
        this.isSearching = false;
      },
      error: () => {
        this.companySearchResults = [];
        this.isSearching = false;
        this.showResults = false;
      }
    });
  }

  selectCompany(company: CompanyResponse) {
    this.selectedCompany = company;
    this.companySearch = company.company_name;
    this.showResults = false;
  }

  clearSelectedCompany() {
    this.selectedCompany = null;
    this.companySearch = '';
  }

  get isCompanyInvalid(): boolean {
    return this.submitted && !this.selectedCompany;
  }

  get isFormValid(): boolean {
    return !!this.selectedCompany && !!this.accessLevel;
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('manageEvent.coOrganisers.validationError'));
      return;
    }

    this.saving = true;

    this.eventsService.addEventCoOrganiser({
      event_id: this.event.event_id,
      co_organiser_company_id: this.selectedCompany!.company_id,
      access_level: this.accessLevel
    }).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.toastService.success(this.translateService.instant('manageEvent.coOrganisers.addSuccess'));
        this.modal.close(res);
      },
      error: (err) => {
        this.saving = false;
        // Check if it's a network/CORS error (backend endpoint not implemented yet)
        if (!err.error?.reason && (err.status === 0 || err.status === 404)) {
          this.toastService.error('Backend API not implemented yet. Please implement the events/co-organisers endpoint.');
        } else {
          this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.coOrganisers.addError'));
        }
      }
    });
  }

  close() {
    this.modal.close();
  }
}
